import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styles from './CustomizeUnits.module.scss';
import { SelectOption } from '../../types';
import Spinner from 'src/components/spinner/Spinner';
import Button from 'src/components/button/Button';
import {
  UnitContentFragment,
  SchoolDataFragment,
  Unit,
  UnitsByProgram,
  useGetAllUnitsQuery,
  useGetAllUnitsAndSllLessonsQuery,
  SllLesson,
  AusCourseContent,
  CourseContentType
} from '../../api/graphql-codegen';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import GridUnitsLayout from './gridLayout/GridUnitsLayout';
import FilterLayout from './filterLayout/FilterLayout';
import { useQueryDictionary } from '../../hooks/useQueryDictionary';
import ArrowDownIcon from 'src/assets/icons/arrow_down.svg?react';
import Card from './card/Card';
import { contentOptions, countryOptions } from '@src/constants';
import CoverUnit from '../CoverUnit/CoverUnit';
import CoverLesson from '../CoverLesson/CoverLesson';
import GridAusCourseContentLayout from './gridLayout/GridAusCourseContentLayout';
import { useToast } from "@src/hooks/useToast";
import { logger } from '../../utils/logger';

const unitMoveMessage = 'Content is moved successfully!';

type Props = {
  handleOpenNew?: (primary: any[], additional: any[], primarySllLessons?: any[]) => void  // pass added units
  handleCancel?: () => void                     // cancel button action
  type?: string                                 // custome type
  course?: any                     // course
  school?: SchoolDataFragment                   // school
  isDisabled?: boolean                               // 'Done' button state
  hasNext?: boolean;
  isHsaApp?: boolean;
  isSchoolCourse?: boolean;
}

const filterWithKeyword = (units: UnitContentFragment[], keyword: string) =>
  units.filter(unit => unit.name.toLowerCase().includes(keyword))

const findCommonElements = (arr1: any[], arr2: any[]) => {
    return arr1.some(item => arr2.includes(item))
}

function CustomizeUnits(props: Props) {
  const { handleOpenNew, handleCancel, type, course, school, isDisabled, hasNext, isHsaApp=false, isSchoolCourse } = props;

  const [keyword, setKeyword] = useState('');
  const [content, setContent] = useState<string>('');   // Units/Lessons/OPReels
  const [country, setCountry] = useState<string>('All');
  const [courses, setCourses] = useState<string[]>([]);
  const [gradeContents, setGradeContents] = useState<UnitContentFragment[]>([]);
  const [additionContents, setAdditionContents] = useState<UnitContentFragment[]>([]);
  const [isMovable, setIsMovable] = useState(false);  // New Cover movable
  const [gradeCourseContents, setGradeCourseContents] = useState<AusCourseContent[]>([]);
  const { successToast } = useToast();

  const isAusCourse = useMemo(() => course?.country === countryOptions[1].label, [course?.country])
  const {data: mainUnits, loading } = useGetAllUnitsQuery({
    variables: { input: { keyword: '', sortBy: 'Name', order: 'Ascending' }},
    fetchPolicy: 'cache-first',
  });
  const {data: mainUnits1, loading: unitAndLessonLoading} = useGetAllUnitsAndSllLessonsQuery({
    fetchPolicy: 'cache-first',
  })

  const coursesOptions = useQueryDictionary({ dictionary: 'Programs' });

  useEffect(() => {
    document.getElementById("root")!.style.overflow = "hidden"
    document.getElementById("root")!.style.height = "auto"
  }, []);

  const allUnits = useMemo(() => {
    return mainUnits1?.getAllUnitsAndSLLLessons?.units as Unit[] ?? []
  }, [mainUnits1?.getAllUnitsAndSLLLessons?.units]);

  const allLessons = useMemo(() => {
    return mainUnits1?.getAllUnitsAndSLLLessons?.sllLessons ?? []
  }, [mainUnits1?.getAllUnitsAndSLLLessons?.sllLessons])

  useEffect(() => {
    if (isSchoolCourse) {
      if (course && course.primary && course.primary.length > 0) {
        const newGradeContents = course.primary.map(p => allUnits.find(u => u.id === p) || p);
        setGradeContents(newGradeContents);
      }
      if (course && course.additional && course.additional.length > 0) {
        const newAdditionContents= course.additional.map(p => allUnits.find(u => u.id === p) || p);
        setAdditionContents(newAdditionContents);
      }
      if (isAusCourse && course && course?.courseContents && course.courseContents.length > 0) {
        setGradeCourseContents(course?.courseContents ?? []);
      }
    } else {
      if (course && course.primary && course.primary.length > 0 && allUnits.length > 0) {
        const ids = course.primary.map(u => u.id);
        const existingUnits = allUnits.filter(e => e?.id && ids.includes(e.id));
        setGradeContents(existingUnits);
      }
      if (course && course.additional && course.additional.length > 0 && allUnits.length > 0) {
        const ids = course.additional.map(u => u.id);
        const existingUnits = allUnits.filter(e => e?.id && ids.includes(e.id));
        setAdditionContents(existingUnits);
      }
      if (isAusCourse && course && course?.courseContents && course.courseContents.length > 0) {
        setGradeCourseContents(course?.courseContents ?? []);
      }
    }
  }, [course, isSchoolCourse, isAusCourse, allUnits]);
  const handleChangeSearch = useCallback((v) => {
    setKeyword(v);
  }, []);

  const handleResetFilters = useCallback(() => {
    setContent('')
    setCountry('');
    setKeyword('');
    setCourses([]);
  }, []);

  const handleChangeContent = useCallback((option: SelectOption) => {
    setContent(option.value);
  },[])
  const handleChangeCountry = useCallback((option: SelectOption) => {
    setCountry(option.value);
  },[])

  const handleChangeOptions = (value: any) => {
    setCourses(value);
  };

  const mainfiltered = useMemo(() => {
    const s = keyword.toLowerCase();
    var newUnits: UnitsByProgram[] = [];
    var totalUnits = 0;
    mainUnits?.getAllUnits.forEach(item=>{
      var courseUnits = filterWithKeyword((item.units || []), s);
      totalUnits = totalUnits + courseUnits.length;
      if (courseUnits.length > 0) {
        newUnits.push({id: item.id, name: item.name, units: [...courseUnits] })
      }
    })

    if (courses && courses.length > 0) {
      newUnits = newUnits.filter(course => course?.id && courses.includes(course?.id));
    }
    return newUnits
  }, [keyword, mainUnits?.getAllUnits, courses]);

  const {filteredUnits, filteredLessons} = useMemo(() => {
    var filteredUnits = mainUnits1?.getAllUnitsAndSLLLessons?.units ?? []
    var filteredLessons = mainUnits1?.getAllUnitsAndSLLLessons?.sllLessons ?? []
    if (keyword) {
      filteredUnits = filteredUnits.filter(unit => unit?.name.toLowerCase().includes(keyword.toLowerCase()))
      filteredLessons = filteredLessons.filter(lesson => lesson?.name.toLowerCase().includes(keyword.toLowerCase()))
    }

    if (country && country !== 'All') {
      const countryName = countryOptions.find(c => c.value === country)?.label ?? ""
      filteredUnits = filteredUnits.filter(unit => (unit?.country || '').includes(countryName));
      filteredLessons = filteredLessons.filter(lesson => (lesson?.country || '').includes(countryName));
    }
    
    if (content) {
      if (content === contentOptions[0].value) {
        filteredLessons = [];
      } else if (content === contentOptions[1].value) {
        filteredUnits = []
      } else if (content === contentOptions[2].value) {
        filteredUnits = filteredUnits.filter(unit => unit?.isNewCover === true);
        filteredLessons = filteredLessons.filter(lesson => lesson?.isOpReel === true);
      }
    }

    if (courses && courses.length > 0) {
      filteredUnits = filteredUnits.filter(unit => unit?.courseId && findCommonElements(courses, JSON.parse(unit?.courseId)));
      filteredLessons = filteredLessons.filter(lesson => lesson?.ageGroup && courses.includes(lesson?.ageGroup.toLowerCase()))
    }

    return {filteredUnits, filteredLessons}
  }, [keyword, country, content, mainUnits1?.getAllUnitsAndSLLLessons, courses])

  // Add all units to Grade
  const handleAddAllUnits = () => {
    const newUnits = allUnits.slice()
    setGradeContents(newUnits)
  }
  // Add Unit to Grade
  const handleAddUnitToGrade = useCallback((unit: UnitContentFragment) => {
    setGradeContents((prev: any) => {
      const preUnits = prev as UnitContentFragment[];
      const exist = preUnits.find(item => item.id === unit.id)
      const existOnAddition = additionContents.find(item => item.id === unit.id)
      if (exist || existOnAddition) {
        return prev;
      } else {
        successToast(unitMoveMessage);
        return [...prev, unit];
      }
    })
  }, [additionContents, successToast])
  // Add Unit to Addition
  const handleAddUnitToAddition = useCallback((unit: UnitContentFragment) => {
    setAdditionContents((prev: any) => {
      const preUnits = prev as UnitContentFragment[];
      const exist = preUnits.find(item => item.id === unit.id)
      const existOnGrad = gradeContents.find(item => item.id === unit.id)
      if (exist || existOnGrad) {
        return prev;
      } else {
        successToast(unitMoveMessage);
        return [...prev, unit];
      }
    })
  },[gradeContents, successToast])
  // Move Unit from Grade to Addition
  const handleMoveUnitFromGradeToAddition = (unit: UnitContentFragment) => {
    setAdditionContents((prev: any) => {
      const preUnits = prev as UnitContentFragment[];
      const exist = preUnits.find(item => item.id === unit.id)
      if (exist) {
        return [...prev];
      } else {
        return [...prev, unit];
      }
    })
    const temp = gradeContents.slice()
    setGradeContents(temp.filter(u => u.id !== unit.id));
  }
  // Move Unit from Addition to Grade
  const handleMoveUnitFromAdditionToGrade = (unit: UnitContentFragment) => {
    setGradeContents((prev: any) => {
      const preUnits = prev as UnitContentFragment[];
      const exist = preUnits.find(item => item.id === unit.id)
      if (exist) {
        return [...prev];
      } else {
        return [...prev, unit];
      }
    })
    const temp = additionContents.slice()
    setAdditionContents(temp.filter(u => u.id !== unit.id));
  }
  // Clear all Units
  const handleRemoveAllUnits = () => {
    setGradeContents([])
    setAdditionContents([])
    setGradeCourseContents([]);
  }

  // Remove Grade Unit
  const handleRemoveGradeUnit = (unit: UnitContentFragment) => {
    setGradeContents((prev: any) => {
      const preUnits = prev as UnitContentFragment[];
      const temp = preUnits.filter(item => item !== unit);
      return temp;
    })
  }
  // Remove Addition Unit
  const handleRemoveAdditionUnit = (unit: UnitContentFragment) => {
    setAdditionContents((prev: any) => {
      const preUnits = prev as UnitContentFragment[];
      const temp = preUnits.filter(item => item !== unit);
      return temp;
    })
  }
  // Remove Grade Lesson
  const handleRemoveAusGradeContent = (content: AusCourseContent) => {
    setGradeCourseContents((prev: any) => {
      const preLessons = prev as AusCourseContent[];
      const temp = preLessons.filter(item => item !== content);
      return temp;
    })
  }
  // handle Drag and Drop
  const handleDrop = useCallback(
    (index: number, item: any) => {
      try {
        if (item.unit) {  // Drop with Unit
          const dropObj = Object.values(item) && Object.values(item)[0] as UnitContentFragment
          const droppedUnit = allUnits.find(unit => unit.id === dropObj.id)
          if (index === 0) {  // Remove units
            if (isAusCourse) {
              if (gradeCourseContents.find(item => item.id === droppedUnit?.id)) {
                const temp = [...gradeCourseContents];
                setGradeCourseContents(temp.filter(u => u.id !== droppedUnit?.id));
              }
            } else {
              if (gradeContents.find(item => item.id === droppedUnit?.id)) {
                const temp = [...gradeContents];
                setGradeContents(temp.filter(u => u.id !== droppedUnit?.id));
              } else if (additionContents.find(item => item.id === droppedUnit?.id)) {
                const temp = [...additionContents];
                setAdditionContents(temp.filter(u => u.id !== droppedUnit?.id));
              }
            }
          } else if (index === 1) {  // Add Unit to Grade Content
            if (isAusCourse) {
              setGradeCourseContents((prev: any) => {
                const preLessons = prev as AusCourseContent[];
                const exist = preLessons.find(item => item.id === droppedUnit?.id)
                if (exist) {
                  return [...prev];
                } else {
                  const addingContent: AusCourseContent = {
                    id: droppedUnit?.id ?? "",
                    categories: droppedUnit?.categories ?? "",
                    comingSoonAt: droppedUnit?.comingSoonAt ?? "",
                    coverImage: droppedUnit?.coverImage ?? "",
                    gradeLevel: "",
                    isComingSoon: droppedUnit?.isComingSoon ?? false,
                    isNew: droppedUnit?.isNew ?? false,
                    isNewCover: droppedUnit?.isNewCover ?? false,
                    isOpReel: false,
                    isUpdated: droppedUnit?.isUpdated ?? false,
                    name: droppedUnit?.name ?? "",
                    number: droppedUnit?.number ?? 0,
                    progress: droppedUnit?.progress ?? 0,
                    subHeading: droppedUnit?.subHeading ?? "",
                    suggestedDuration: droppedUnit?.suggestedDuration ?? "",
                    suggestedLessonCount: droppedUnit?.suggestedLessonCount ?? 0,
                    type: CourseContentType.UNIT,
                    comingSoonLabelPosition: droppedUnit?.comingSoonLabelPosition ?? "",
                    newLabelPosition: droppedUnit?.newLabelPosition ?? "",
                    updatedLabelPosition: droppedUnit?.updatedLabelPosition ?? ""
                  }
                  return [...prev, addingContent];
                }
              })
            } else {
              setGradeContents((prev: any) => {
                const preUnits = prev as UnitContentFragment[];
                const exist = preUnits.find(item => item.id === droppedUnit?.id)
                if (exist) {
                  return [...prev];
                } else {
                  return [...prev, droppedUnit];
                }
              })
              const temp = additionContents.slice()
              setAdditionContents(temp.filter(u => u.id !== droppedUnit?.id));
            }
          } else if (index === 2) { // Add Unit to Additional Content
            setAdditionContents((prev: any) => {
              const preUnits = prev as UnitContentFragment[];
              const exist = preUnits.find(item => item.id === droppedUnit?.id)
              if (exist) {
                return [...prev];
              } else {
                return [...prev, droppedUnit];
              }
            })
            const temp = gradeContents.slice()
            setGradeContents(temp.filter(u => u.id !== droppedUnit?.id));
          }
          successToast(unitMoveMessage);
        } else if (item.lesson) { // Drop with Standalone Lesson

          const dropObj = Object.values(item) && Object.values(item)[0] as SllLesson
          const droppedLesson = allLessons.find(lesson => lesson?.id === dropObj.id)
          
          if (index === 0) {  // Remove lesson
            if (gradeCourseContents.find(item => item.id === droppedLesson?.id)) {
              const temp = [...gradeCourseContents];
              setGradeCourseContents(temp.filter(u => u.id !== droppedLesson?.id));
            }
          } else if (index === 1) {  // Add Lesson to Main Content
            setGradeCourseContents((prev: any) => {
              const preLessons = prev as AusCourseContent[];
              const exist = preLessons.find(item => item.id === droppedLesson?.id)
              if (exist) {
                return [...prev];
              } else {
                const addingContent: AusCourseContent = {
                  id: droppedLesson?.id ?? "",
                  categories: droppedLesson?.categories ?? "",
                  comingSoonAt: droppedLesson?.comingSoonAt ?? "",
                  coverImage: droppedLesson?.coverImage ?? "",
                  gradeLevel: droppedLesson?.gradeLevel ?? "",
                  isComingSoon: droppedLesson?.isComingSoon ?? false,
                  isNew: droppedLesson?.isNew ?? false,
                  isNewCover: droppedLesson?.isNewCover ?? false,
                  isOpReel: droppedLesson?.isOpReel ?? false,
                  isUpdated: droppedLesson?.isUpdated ?? false,
                  name: droppedLesson?.name ?? "",
                  number: 0,
                  progress: droppedLesson?.progress ?? 0,
                  subHeading: "",
                  suggestedDuration: droppedLesson?.suggestedDuration ?? "",
                  suggestedLessonCount: 0,
                  type: CourseContentType.LESSON,
                  comingSoonLabelPosition: droppedLesson?.comingSoonLabelPosition ?? "",
                  newLabelPosition: droppedLesson?.newLabelPosition ?? "",
                  updatedLabelPosition: droppedLesson?.updatedLabelPosition ?? ""
                }
                return [...prev, addingContent];
              }
            })
          }
          successToast(unitMoveMessage);
        }
      } catch (e) {
        logger.info(e);
      }
    }, [allUnits, gradeContents, additionContents, gradeCourseContents, allLessons, isAusCourse, successToast]
  );

  const handleAddUnit = () => {}

  const gradeTitle = useMemo(() => {
    let title = "Grade 1";

    if (course?.name) {
      title = course.name;
    }
    return title;
  }, [course?.name]);

  const topTitle = useMemo(() => {
    let title = `Customize Units${course?.name ? ` for ${course?.name}` : ''} for school`
    switch (type) {
      case "course":
        if (course?.name) {
          title = `Customize Units for ${course.name}`
        } else {
          title = ''
        }
        break;
      case "school":
        if (course?.name) {
          title = `Customize Units for ${course.name} for ${school?.name}`
        } else {
          title = ''
        }
        break;
      default:
        break;
    }
    return title;
  }, [course, school, type])

  const nextLabel = useMemo(() =>{
    let title = 'Done';
    if (type === 'course') {
      if (course?.primary && course?.additional) {
        title = (course.primary.length > 0 || course.additional.length > 0) ? 'Save Course' : 'Create Course';
      } else if (course?.courseContents && course.courseContents.length > 0) {
        title = 'Save Course';
      } else {
        title = 'Create Course';
      }
    } else {
      title = hasNext ? 'Next' : 'Done';
    }
    return title;
  }, [course, type, hasNext])

  const gradeUnits = useMemo(() => gradeContents ?? [], [gradeContents])
  const additionUnits = useMemo(() => additionContents ?? [], [additionContents])

  const unitContent = (
    <div>
      {mainfiltered.map((item, i) => {
        return (
          <div key={i}>
            <div className={styles.courseTitle}>{item.name}</div>
            <div className={styles.unitsDiv}>
              {((item as UnitsByProgram).units || []).map((unit, index) => {
                return (
                  <Card
                    key={index}
                    unit={unit}
                    type={"type"}
                    source={"origin"}
                    target={gradeTitle}
                    handleLeftArrowAction={handleAddUnitToGrade}
                    handleRightArrowAction={handleAddUnitToAddition}
                  />
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
  const handleMovableNewCover = (isNew: boolean) => {
    setIsMovable(isNew)
  }

  const unitAndLessonContent = (
    <div>
      <div className={styles.subTitle}>Add Units or Lessons from Below:</div>
      <div className={styles.unitsDiv}>
        {filteredUnits.map((unit, index) => {
          return (
            <CoverUnit
              key={index}
              unit={unit as UnitContentFragment}
              type={"type"}
              source={"origin"}
              target={gradeTitle}
              handleLeftArrowAction={handleAddUnitToGrade}
              handleRightArrowAction={handleAddUnitToAddition}
              handleMovableNewCover={handleMovableNewCover}
            />
          )
        })}
      </div>
      <div className={styles.unitsDiv}>
      {filteredLessons.map((lesson, index) => {
        return (
          <CoverLesson
            key={index}
            lesson={lesson as SllLesson}
            type={"type"}
            source={"origin"}
            target={gradeTitle}
            // handleLeftArrowAction={handleAddUnitToGrade}
            // handleRightArrowAction={handleAddUnitToAddition}
            handleMovableNewCover={handleMovableNewCover}
          />
        )
      })}
      </div>
    </div>
  )

  return (
    <DndProvider backend={HTML5Backend}>
      {loading || unitAndLessonLoading ? <div className={styles.spinner}><Spinner /></div> : <>
      {topTitle && <div className={styles.header}>
          <div className={styles.title}>{topTitle}</div>
          <div className={styles.actionsBanner}>
            <div className={styles.actionsBanner_buttons}>
              <Button className={styles.actionsBanner_cancel} onClick={handleCancel} size={'small'} label={'Cancel'} />
              <Button 
                disabled={isDisabled || ( isAusCourse ? gradeCourseContents.length === 0 : gradeContents.length === 0)} 
                leftIcon={isDisabled ? <Spinner width={20} height={20} /> : undefined} className={styles.actionsBanner_done} 
                onClick={() => handleOpenNew && handleOpenNew(gradeUnits, additionUnits, gradeCourseContents)} size={'small'} label={nextLabel} />
            </div>
          </div>
        </div>}
      <div id="customunits" className={styles.page}>
        <div className={styles.leftView}>
          {
          <FilterLayout
            keyword={keyword}
            hideCourse
            content={content}
            country={country}
            coursesOptions={coursesOptions}
            courses={courses}
            isAusCourse={isAusCourse}
            setCourses={setCourses}
            handleChangeSearch={handleChangeSearch}
            handleChangeContent={handleChangeContent}
            handleChangeCountry={handleChangeCountry}
            handleChangeFilter={handleChangeOptions}
            handleResetFilters={handleResetFilters}
            handleRemoveAllUnits={handleRemoveAllUnits}
            handleAddAllUnits={handleAddAllUnits}/>
          }
          {isAusCourse ? unitAndLessonContent : unitContent}
        </div>
        <div className={styles.rightView}>
          { isHsaApp ? <>
            <div className={styles.itemWrapper}>
              <div className={styles.addItem} onClick={handleAddUnit}>
                <div className={styles.addTitle}>+</div>
              </div>
              <div className={styles.addName}>
              {"Select Units for a Child"}
              </div>
            </div>
          </> :
            <>
              <div className={styles.clearButton} onClick={handleRemoveAllUnits}><ArrowDownIcon className={styles.leftArrow} />Clear All</div>
              {isAusCourse ? (
                <GridAusCourseContentLayout
                  source={"grade"}
                  contents={gradeCourseContents}
                  isAusCourse={isAusCourse}
                  isNewMovable={isMovable}
                  handleDrop={handleDrop}
                  handleRemoveContentAction={handleRemoveAusGradeContent}
                />
              ) : (
                <>
                  <div id="grade" className={styles.unitGroupTitle}>{gradeTitle}</div>
                  <GridUnitsLayout 
                    source={"grade"}
                    units={gradeContents}
                    isAusCourse={isAusCourse}
                    isNewMovable={isMovable}
                    handleDrop={handleDrop}
                    handleLeftArrowAction={handleMoveUnitFromGradeToAddition}
                    handleRightArrowAction={handleRemoveGradeUnit}
                  />
                  <div id="addition" className={styles.unitGroupTitle}>Additional Content</div>
                  <GridUnitsLayout
                    source={"addition"}
                    target={gradeTitle}
                    units={additionContents}
                    isAusCourse={isAusCourse}
                    handleDrop={handleDrop}
                    handleLeftArrowAction={handleMoveUnitFromAdditionToGrade}
                    handleRightArrowAction={handleRemoveAdditionUnit}
                  />
                </>
              )}
            </>
          }
        </div>
      </div>
      </>}
    </DndProvider>
  );
}

export default CustomizeUnits;
